import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "@/lib/api";
import { Flame, ArrowUpRight, Sparkles } from "lucide-react";

export default function CreatorProfile({ handleOverride }) {
  const params = useParams();
  const handle = handleOverride || params.handle;
  const [profile, setProfile] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!handle) { setNotFound(true); setLoading(false); return; }
    fetch(`${API}/creator/${handle}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setProfile(d))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [handle]);

  if (loading) return <div className="min-h-screen bg-void text-white p-10 font-mono text-xs text-zinc-500">// loading creator...</div>;
  if (notFound) return (
    <div className="min-h-screen bg-void text-white flex items-center justify-center p-6">
      <div className="max-w-md text-center brutal-border bg-[#141414] p-10 space-y-4" data-testid="creator-notfound">
        <h1 className="heading-display text-3xl">NOT FOUND.</h1>
        <p className="font-mono text-xs text-zinc-400">This creator profile doesn&apos;t exist or is set to private.</p>
        <Link to="/" className="inline-block bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-5 py-3 hover:bg-signal-yellow-hover transition-colors">
          Home →
        </Link>
      </div>
    </div>
  );

  const refLink = `${window.location.origin}/auth?ref=${profile.handle}`;

  return (
    <div className="min-h-screen bg-void text-white" data-testid="creator-page">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-white/10">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 bg-signal-yellow text-black flex items-center justify-center font-mono font-bold">V</div>
          <div>
            <div className="heading-display text-lg leading-none">VIRAL/CO</div>
            <div className="font-mono text-[10px] text-zinc-500 tracking-[0.25em] mt-1">CONTENT MACHINE</div>
          </div>
        </Link>
        <a href={refLink} data-testid="creator-cta-top" className="bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-5 py-2.5 hover:bg-signal-yellow-hover transition-colors">
          Get 30 days free →
        </a>
      </header>

      <section className="px-6 md:px-12 lg:px-20 py-12 md:py-16 border-b border-white/10">
        <div className="label-eyebrow flex items-center gap-2">
          <Flame size={12} className="text-signal-yellow" /> // creator profile · @{profile.handle}
        </div>
        <h1 className="heading-display text-5xl sm:text-7xl mt-3">
          <span className="text-signal-yellow">{profile.first_name}</span>&apos;S<br />TOP HOOKS.
        </h1>
        <p className="font-mono text-xs sm:text-sm text-zinc-400 mt-5 max-w-2xl">
          // real content, made on Viral/Co. want yours featured? sign up + start scoring 80+.
        </p>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-10">
        {profile.hooks.length === 0 ? (
          <div className="brutal-border p-10 bg-[#141414] text-center max-w-2xl mx-auto">
            <div className="font-mono text-xs text-zinc-500">// no top hooks yet. check back soon.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {profile.hooks.map((h, i) => (
              <article key={i} data-testid={`creator-hook-${i}`} className="brutal-border bg-[#141414] p-5 space-y-3 hover:border-signal-yellow transition-colors">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">{h.platform}</span>
                  {h.score && <span className="font-mono text-lg font-bold text-signal-yellow">{h.score}/100</span>}
                </div>
                <p className="font-mono text-sm text-zinc-100 leading-relaxed whitespace-pre-wrap line-clamp-6">{h.content}</p>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-20 border-t border-white/10 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div>
          <h2 className="heading-display text-3xl sm:text-5xl max-w-xl">MAKE YOUR OWN VIRAL HOOKS.</h2>
          <p className="font-mono text-xs text-zinc-500 mt-3 tracking-wider uppercase">// {profile.first_name} sent you — grab 30 days of Pro free</p>
        </div>
        <a href={refLink} data-testid="creator-cta-bottom" className="bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-6 py-4 hover:bg-signal-yellow-hover transition-colors inline-flex items-center gap-2">
          <Sparkles size={12} /> Claim 30 days <ArrowUpRight size={12} />
        </a>
      </section>

      <footer className="px-6 md:px-12 lg:px-20 py-8 border-t border-white/10 font-mono text-[10px] text-zinc-600 tracking-[0.2em] uppercase flex justify-between">
        <span>© VIRAL/CO — viral-co-hq.com — 2026</span>
        <Link to="/support" className="hover:text-signal-yellow transition-colors">Support</Link>
      </footer>
    </div>
  );
}
